import { memo } from "react";
import { Box, Tooltip, useTheme } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

const FollowersCell = ({ row, showNumber = false }) => {
  const theme = useTheme();

  const formatNumber = (value) => {
    if (value == null || value === "") return "n/a";
    const num = Number(value);
    return Number.isNaN(num) ? String(value) : new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(num);
  };

  const getChangeInfo = () => {
    // For detail rows, compare with parent row followers
    if (row.isDetailRow && row.originalRow) {
      const currentFollowers = parseFloat(row.followers || 0);
      const parentFollowers = parseFloat(row.originalRow.followers || 0);
      if (!currentFollowers || !parentFollowers) return null;
      const difference = currentFollowers - parentFollowers;
      const percentage = parentFollowers > 0 ? (difference / parentFollowers) * 100 : 0;
      const isPositive = difference > 0;
      const hasChange = difference !== 0;
      if (!hasChange) {
        return null;
      }
      const direction = isPositive ? "up" : "down";
      const color = isPositive ? theme.palette.success.main : theme.palette.error.main;
      return {
        direction,
        color,
        icon: direction === "up" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />,
        percentage: Math.abs(percentage).toFixed(1),
      };
    }
    // For main rows, use existing monthly growth data
    const diff = parseFloat(row.monthly_growth_followers || 0);
    const perc = parseFloat(row.monthly_growth_percentage || 0);
    if (!diff && !perc) return null;
    const isPositive = diff > 0 || perc > 0;
    const hasChange = diff !== 0 || perc !== 0;
    if (!hasChange) {
      return null;
    }
    const direction = isPositive ? "up" : "down";
    const color = isPositive ? theme.palette.success.main : theme.palette.error.main;
    return {
      direction,
      color,
      icon: direction === "up" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />,
    };
  };

  const getTooltipText = () => {
    // For detail rows, show comparison with parent
    if (row.isDetailRow && row.originalRow) {
      const currentFollowers = parseFloat(row.followers || 0);
      const parentFollowers = parseFloat(row.originalRow.followers || 0);
      if (!currentFollowers || !parentFollowers) return null;
      const difference = currentFollowers - parentFollowers;
      const percentage = parentFollowers > 0 ? (difference / parentFollowers) * 100 : 0;
      if (percentage === 0) return null;
      const formattedDiff = formatNumber(Math.abs(difference));
      return `${difference > 0 ? "+" : "-"}${formattedDiff} followers from main row (${percentage > 0 ? "+" : ""}${percentage.toFixed(1)}%)`;
    }
    // For main rows, use existing monthly growth data
    const perc = parseFloat(row.monthly_growth_percentage || 0);
    if (!perc) return null;
    if (perc === 0) return null;
    return `${perc > 0 ? "+" : ""}${perc}% this month`;
  };

  const changeInfo = getChangeInfo();
  const tooltipText = getTooltipText();

  return (
    <Tooltip title={tooltipText} arrow placement="top" disableHoverListener={!tooltipText}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {showNumber && formatNumber(row.followers)}
        {changeInfo && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              marginLeft: showNumber ? "4px" : "0px",
              color: changeInfo.color,
            }}
          >
            {changeInfo.icon}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default memo(FollowersCell);
